<template>
  <v-app>
    <mainMenu></mainMenu>
    <v-container fluid>

      <proposal-panel v-if="blockingProposals.length" :only-rows="blockingProposals" />

      <v-card class="mb-4">
        <v-toolbar color="orange accent-3" dark dense flat>
          <v-toolbar-title>จัดรถอัตโนมัติ</v-toolbar-title>
          <v-spacer />
          <v-chip v-if="plan" small dark color="orange darken-3">
            {{ plan.summary.jobs }} จุด<template v-if="plan.summary.bookings > plan.summary.jobs"> ({{ plan.summary.bookings }} การจอง)</template>
            / {{ plan.summary.cars }} คัน
          </v-chip>
        </v-toolbar>

        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" sm="4" md="3">
              <v-menu v-model="dateMenu" :close-on-content-click="false" min-width="290">
                <template v-slot:activator="{ on }">
                  <v-text-field :value="date" label="วันที่จัดส่ง" prepend-inner-icon="mdi-calendar"
                                readonly v-on="on" color="orange accent-3" hide-details="auto" />
                </template>
                <v-date-picker v-model="date" color="orange accent-3" locale="th"
                               @input="dateMenu = false; reset()" />
              </v-menu>
            </v-col>

            <!-- Digits only, and the colon puts itself in: type 0630 (or just
                 630) and the field reads 06:30. Focus selects the whole value,
                 so replacing it is a click and four digits -- which is why
                 there is no clear button: empty is not a state this form can
                 plan from. No hint line here either, because a persistent hint
                 adds height under this field alone and lifts it out of line
                 with the date beside it. -->
            <v-col cols="6" sm="3" md="2">
              <v-text-field v-model="departAt" label="เวลาออกจากคลัง" color="orange accent-3"
                            hide-details="auto" placeholder="08:30" prepend-inner-icon="mdi-clock-outline"
                            inputmode="numeric" maxlength="5" @blur="completeDepart"
                            @focus="$event.target.select()" />
            </v-col>

            <v-col cols="12" sm="5" md="7" class="text-sm-right">
              <v-btn color="orange accent-3" dark :loading="planning" @click="runPlan()" class="mr-2">
                <v-icon left>mdi-map-search</v-icon>วางแผน
              </v-btn>
              <v-btn v-if="plan && plan.summary.jobs" color="success" :loading="committing"
                     :disabled="blockingProposals.length > 0 || dispatched.length > 0"
                     @click="askCommit" class="mr-2">
                <v-icon left>mdi-content-save-check</v-icon>ยืนยันแผนนี้
              </v-btn>
              <v-btn v-if="assignedCount" text color="error" :loading="clearing" @click="askClear">
                <v-icon left>mdi-delete-sweep</v-icon>ล้างแผน
              </v-btn>
            </v-col>
          </v-row>

          <!-- Each plan is the best of several runs (dispatchPlan.planRuns), so
               วางแผน takes a few seconds; this says it is working. -->
          <div v-if="planning && planProgress.total" class="mt-2">
            <div class="caption orange--text text--darken-3 mb-1">
              กำลังวางแผน {{ planProgress.done }}/{{ planProgress.total }} รอบ — ลองหลายแบบแล้วเลือกแผนที่ถูกที่สุด
            </div>
            <v-progress-linear color="orange accent-3" height="4" rounded
                               :value="100 * planProgress.done / planProgress.total" />
          </div>

          <div v-if="blockingProposals.length" class="caption red--text mt-1">
            มีคำขอเลื่อน/ยกเลิก {{ blockingProposals.length }} รายการในวันนี้ — ยืนยันหรือปฏิเสธก่อนจึงจะยืนยันแผนได้
          </div>

          <!-- TRANSITION PERIOD (dispatchPlan.dispatchedConflicts): SOs of
               this day already on คิวรถเล็ก. ยืนยันแผนนี้ would write them a
               second time, so it stays disabled, and so does ส่งเข้าคิวจัดรถ
               on a route holding one. Delete this card with the rule. -->
          <v-alert v-if="dispatched.length" dense type="error" text class="mt-3 mb-0">
            <!-- A count, not a list: each such row below is tinted red and says
                 which car it is actually on, which is where someone decides. -->
            <div class="font-weight-medium">
              SO ของวันนี้อยู่ในคิวรถเล็กแล้ว {{ dispatched.length }} งาน (แถวสีแดงด้านล่าง)
              — ยืนยันแผนนี้ไม่ได้ เพราะจะสร้างงานซ้ำ
            </div>
            <div class="caption mt-1">
              ทางเลือก: ลบงานเหล่านี้ที่หน้าคิวรถเล็ก แล้วกดวางแผนใหม่ —
              หรือจัดรถวันนี้ด้วยมือต่อไป โดยไม่ต้องยืนยันแผนนี้
            </div>
          </v-alert>

          <!-- Read-only until ยืนยัน. Said plainly, because a tool that
               rearranges the schedule the moment it is opened is one nobody
               trusts. -->
          <v-alert dense text type="info" class="mt-3 mb-0">
            การกดปุ่ม "วางแผน" เป็นการคำนวณเท่านั้น ยังไม่มีการบันทึกใดๆ จนกว่าจะกด "ยืนยันแผนนี้"
            <span v-if="assignedCount"> — ขณะนี้มีแผนที่บันทึกไว้แล้ว {{ assignedCount }} งาน</span>
          </v-alert>
        </v-card-text>
      </v-card>

      <v-alert v-if="error" dense text type="error" class="mb-4">{{ error }}</v-alert>

      <template v-if="plan">
        <!-- Two plans, when they differ. Clicking one shows its routes below;
             ยืนยันแผนนี้ commits the one shown. ชาร์จน้อยที่สุด is selected on
             every วางแผน, so saving fuel is always a deliberate choice. -->
        <v-card v-if="plans && plans.minFuel" class="mb-4">
          <v-card-text class="py-3">
            <div class="caption grey--text mb-2">เลือกแผนเพื่อดูเส้นทาง — ยืนยันแผนนี้จะบันทึกแผนที่เลือกอยู่</div>
            <v-row dense>
              <v-col v-for="opt in planChoices" :key="opt.mode" cols="12" sm="6">
                <v-card outlined class="plan-choice" :class="{ 'plan-choice--on': mode === opt.mode }"
                        @click="selectPlan(opt.mode)">
                  <v-card-text class="py-2">
                    <div class="subtitle-1 font-weight-medium">
                      <v-icon small :color="mode === opt.mode ? 'orange accent-3' : 'grey'">
                        {{ mode === opt.mode ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                      </v-icon>
                      {{ opt.title }}
                    </div>
                    <div class="caption grey--text mb-1">{{ opt.hint }}</div>
                    <v-row dense>
                      <v-col v-for="row in opt.rows" :key="row.label" cols="6">
                        <div class="caption grey--text">{{ row.label }}</div>
                        <div class="body-1" :class="{ 'font-weight-bold': row.strong }">{{ row.value }}</div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Summary. The item-count breakdown sits here rather than buried in a
             tooltip, because it is the difference between a timetable that was
             measured and one that only looks measured. -->
        <v-card class="mb-4">
          <v-card-text class="py-3">
            <v-row dense>
              <v-col v-for="s in summaryTiles" :key="s.label" cols="6" sm="4" md="2">
                <div class="caption grey--text">{{ s.label }}</div>
                <div class="title">{{ s.value }}</div>
              </v-col>
            </v-row>

            <div v-if="moveCount" class="mt-2">
              <v-chip small color="indigo darken-1" dark class="mr-2">
                <v-icon left small>mdi-swap-horizontal</v-icon>ย้ายรถด้วยมือ {{ moveCount }} จุด
              </v-chip>
              <v-btn x-small text color="indigo" :disabled="planning" @click="clearMoves">
                ยกเลิกการย้ายทั้งหมด
              </v-btn>
            </div>

            <v-divider class="my-3" />

            <div class="caption grey--text mb-1">ที่มาของเวลาติดตั้ง</div>
            <!-- A stop staff timed themselves is the most trustworthy row here, not
                 the least: it is the only one where a person who has done the work
                 said how long it takes. It leaves the other three buckets entirely
                 -- see the summary counters in dispatchPlan.js. -->
            <v-chip v-if="items.manual" small class="mr-2" color="green darken-2" dark>
              กำหนดเวลาเอง {{ items.manual }}
            </v-chip>
            <v-chip small class="mr-2" :color="items.explicit ? 'green' : 'grey lighten-2'"
                    :dark="!!items.explicit">
              นับจาก SO / กรอกเอง {{ items.explicit }}
            </v-chip>
            <v-chip small class="mr-2" :color="items.estimated ? 'amber darken-2' : 'grey lighten-2'"
                    :dark="!!items.estimated">
              อ่านจากรายการสินค้า {{ items.estimated }}
            </v-chip>
            <v-chip small :color="items.assumed ? 'red' : 'grey lighten-2'" :dark="!!items.assumed">
              ใช้ค่าเริ่มต้น {{ items.assumed }}
            </v-chip>
            <div v-if="items.assumed" class="caption red--text mt-2">
              งาน {{ items.assumed }} รายการไม่มีข้อมูลจำนวนชิ้น เวลาติดตั้งเป็นการประมาณ
              โปรดตรวจสอบก่อนยืนยัน
            </div>

            <v-divider class="my-3" />

            <!-- The pin is the one thing a driver's นำทาง goes to directly; without
                 it the driver is navigating by the written address. Counted here
                 so a day is checked before it is confirmed, and each such stop
                 carries the ไม่มีพิกัด chip in its row below. -->
            <div class="caption grey--text mb-1">พิกัดสำหรับคนขับ</div>
            <v-chip small class="mr-2" :color="pinned ? 'green' : 'grey lighten-2'" :dark="!!pinned">
              <v-icon left small>mdi-map-marker-check</v-icon>มีพิกัด {{ pinned }}
            </v-chip>
            <v-chip small :color="plan.summary.noPin ? 'orange darken-3' : 'grey lighten-2'"
                    :dark="!!plan.summary.noPin">
              <v-icon left small>mdi-map-marker-off-outline</v-icon>ไม่มีพิกัด {{ plan.summary.noPin || 0 }}
            </v-chip>
            <div v-if="plan.summary.noPin" class="caption orange--text text--darken-3 mt-2">
              งาน {{ plan.summary.noPin }} รายการยังไม่มีพิกัด คนขับจะต้องนำทางจากที่อยู่
              กดที่ป้าย ไม่มีพิกัด ของงานเพื่อเพิ่มพิกัด แผนจะคำนวณใหม่ให้ทันที
            </div>
          </v-card-text>
        </v-card>

        <!-- One card per car. -->
        <v-card v-for="route in plan.routes" :key="route.car.regNum" class="mb-3">
          <v-toolbar dense flat :color="fuelColor(route.car.fuelType)" dark>
            <v-toolbar-title class="subtitle-1">
              {{ route.car.regNum }}
              <span class="caption ml-2">{{ carLabel(route.car) }}</span>
            </v-toolbar-title>
            <v-spacer />
            <!-- Editable, because the planner cannot know that this driver has
                 to load in the morning. Typed times win; one that breaks the
                 round is refused and says so rather than being obeyed. -->
            <span class="caption mr-1">ออก</span>
            <v-text-field :value="hm(route.startAt)" @change="setDepart(route, $event)"
                          dense dark flat solo-inverted hide-details
                          class="depart-field mr-2" style="max-width:76px" />
            <v-chip v-if="route.departSource === 'manual'" x-small outlined class="mr-2">
              กำหนดเอง
            </v-chip>
            <v-chip v-else-if="route.departSource === 'manual-rejected'" x-small dark
                    color="red darken-4" class="mr-2">
              เวลาที่กรอกวิ่งไม่ได้
            </v-chip>
            <!-- The planner sent this car before the normal departure, because
                 the round pays for the overtime (a far เช้า job, or a day that
                 would otherwise need another car). Said, so it is not read as
                 a typo. -->
            <v-chip v-else-if="route.startAt < plan.config.departMinutes" x-small dark
                    color="deep-orange darken-2" class="mr-2">
              ออกก่อนเวลา (มีค่าล่วงเวลา)
            </v-chip>
            <v-chip v-else-if="route.idleTrimmed > 0" x-small outlined class="mr-2">
              เลื่อนออก {{ route.idleTrimmed }}′
            </v-chip>
            <span class="caption mr-3">
              {{ route.stops.length }} งาน · {{ route.km.toFixed(0) }} กม. ·
              ฿{{ route.fuelCost.toFixed(0) }}<template v-if="route.overtimeCost > 0.5"> ·
              ล่วงเวลา {{ route.overtimeMinutes.toFixed(0) }}′ ฿{{ route.overtimeCost.toFixed(0) }}</template>
              · กลับ {{ hm(route.endAt) }}
            </span>
            <v-btn small outlined dark @click="openRouteMap(route)">
              <v-icon left small>mdi-map-marker-path</v-icon>แผนที่
            </v-btn>
          </v-toolbar>

          <!-- plan-table fixes the column layout (see the style block): five
               separate tables have to read as one timetable, so the clock
               columns must land in the same place on every card no matter how
               wide that car's notes happen to be. -->
          <v-simple-table dense class="plan-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width:44px"></th>
                  <th class="text-left" style="width:64px">ลำดับ</th>
                  <th class="text-left" style="width:30%">ลูกค้า / พื้นที่</th>
                  <th class="text-left" style="width:84px">ถึง</th>
                  <th class="text-left" style="width:96px">เริ่ม</th>
                  <th class="text-left" style="width:84px">เสร็จ</th>
                  <th class="text-right" style="width:64px">ชิ้น</th>
                  <th class="text-right" style="width:96px">ติดตั้ง</th>
                  <th class="text-right" style="width:80px">กม.</th>
                  <th style="width:44px"></th>
                  <th class="text-left">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                <!-- Two rows per stop: the timetable line, and -- only when
                     opened -- what that line is made of. The detail is behind a
                     click rather than always on because five cars of product
                     lines is a page nobody can read as a timetable, and the
                     timetable is what this screen is for. -->
                <template v-for="(stop, i) in route.stops">
                  <tr :key="'s' + i" :class="{ 'dup-row': !!dupOf(stop.job.bookings) }">
                    <td>
                      <v-btn icon x-small @click="toggleStop(route, i)">
                        <v-icon small>
                          {{ isStopOpen(route, i) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        </v-icon>
                      </v-btn>
                    </td>
                    <td>{{ i + 1 }}</td>
                    <td>
                      <div>{{ stop.job.booking.name || stop.job.booking.custName || '—' }}</div>
                      <v-chip v-if="bookingsOf(stop).some(RJ.hasReturn)" x-small label outlined
                              color="blue darken-2" class="mr-1">รับคืน</v-chip>
                      <!-- One stop, several SOs: the customer ordered more than
                           once and every order rides on this truck. Each is
                           named, because each is its own job in carBooking. -->
                      <div v-if="bookingsOf(stop).length > 1" class="caption teal--text text--darken-2">
                        รวม {{ bookingsOf(stop).length }} การจอง:
                        {{ bookingsOf(stop).map(b => RJ.refText(b)).join(', ') }}
                      </div>
                      <!-- The written address first, then the ตำบล the planner
                           actually routed by. Both, because they can disagree --
                           and when they do, that is the thing to notice. -->
                      <div v-if="addressOf(stop)" class="caption">{{ addressOf(stop) }}</div>
                      <div class="caption grey--text">{{ placeOf(stop) }}</div>
                      <div v-if="stop.job.pinMismatchKm > plan.config.pinMismatchKm"
                           class="caption deep-orange--text">
                        พิกัดที่บันทึกไว้ห่างจากตำบลนี้ {{ stop.job.pinMismatchKm.toFixed(0) }} กม.
                        — แผนใช้พิกัด
                      </div>
                    </td>
                    <td class="nowrap">{{ hm(stop.arriveAt) }}</td>
                    <!-- The wait goes on its own line rather than wrapping mid
                         phrase, so the start time stays level with ถึง and เสร็จ. -->
                    <td class="nowrap">
                      <div>{{ hm(stop.startAt) }}</div>
                      <div v-if="stop.waitMinutes > 0" class="caption grey--text">
                        รอ {{ Math.round(stop.waitMinutes) }}′
                      </div>
                    </td>
                    <!-- When the WORK is done. A charge, a refuel or lunch taken
                         after it moves when the car leaves, which gets its own
                         line -- showing only the departure here read as an
                         installation that ran until 12:57. -->
                    <td class="nowrap">
                      <div>{{ hm(stop.startAt + stop.installMinutes) }}</div>
                      <div v-if="breakNote(route, stop)" class="caption grey--text">
                        {{ breakNote(route, stop) }}
                      </div>
                    </td>
                    <td class="text-right nowrap">{{ stop.job.items }}</td>
                    <!-- Its own column now, because it is the number staff weigh a
                         round by. The note under it says where the duration came
                         from: a time a person set and a time the tier table
                         guessed look identical otherwise, and only one of them is
                         worth planning a day on. -->
                    <td class="text-right nowrap">
                      <div>{{ stop.installMinutes }}′</div>
                      <div v-if="installNote(stop)" class="caption"
                           :class="installNoteColor(stop)">{{ installNote(stop) }}</div>
                    </td>
                    <td class="text-right nowrap">{{ stop.legKm.toFixed(1) }}</td>
                    <td>
                      <v-btn icon x-small :disabled="!stopMapUrl(stop)" @click="openStopMap(stop)">
                        <v-icon small>mdi-map-marker</v-icon>
                      </v-btn>
                    </td>
                    <td>
                      <v-chip v-if="stop.charge" x-small class="mr-1" color="light-blue" dark>
                        {{ chargeLabel(stop.charge) }}
                      </v-chip>
                      <!-- Where the other plan puts this stop, so switching
                           between the two shows what moved. -->
                      <v-chip v-if="plans && plans.minFuel && stop.otherCar" x-small outlined
                              class="mr-1" color="deep-purple">
                        อีกแผน: {{ stop.otherCar === 'unassigned' ? 'จัดไม่ได้' : stop.otherCar }}
                      </v-chip>
                      <!-- ไม่มีพิกัด opens the พิกัด dialog: the pin is fixed where it is
                           noticed, and the day re-planned around it. -->
                      <v-chip v-if="dupOf(stop.job.bookings)" x-small class="mr-1" color="red darken-1" dark>
                        <v-icon left x-small>mdi-content-duplicate</v-icon>{{ dupText(dupOf(stop.job.bookings)) }}
                      </v-chip>
                      <v-chip v-for="f in stop.flags" :key="f" x-small class="mr-1"
                              :color="flagColor(f)" dark
                              @click="f === 'NO_PIN' ? openPin(stop.job.bookings) : null">
                        <v-icon v-if="f === 'NO_PIN'" left x-small>mdi-map-marker-plus</v-icon>
                        {{ flagText(stop, f) }}
                      </v-chip>
                      <!-- ย้ายไปรถ: each car priced as the whole re-planned
                           day, or greyed with why it cannot take the stop. -->
                      <v-menu offset-y left :close-on-content-click="false"
                              :value="move.key === stopKey(route, i)"
                              @input="closeMove(route, i, $event)">
                        <template v-slot:activator="{ attrs }">
                          <v-btn x-small text color="indigo" v-bind="attrs" :disabled="planning"
                                 @click="openMove(route, i, stop)">
                            <v-icon x-small left>mdi-swap-horizontal</v-icon>ย้ายไปรถ…
                          </v-btn>
                        </template>
                        <v-card min-width="320">
                          <v-card-text class="py-2">
                            <div class="caption grey--text mb-1">
                              ย้ายเฉพาะจุดนี้ รถคันอื่นไม่เปลี่ยน — ค่าใช้จ่าย (น้ำมัน + ล่วงเวลา)
                              ของทั้งวันเทียบกับแผนตอนนี้
                            </div>
                            <div v-if="move.loading" class="py-3 text-center">
                              <v-progress-circular indeterminate size="20" color="indigo" />
                            </div>
                            <div v-if="move.error" class="red--text caption">{{ move.error }}</div>
                            <v-list v-if="move.result" dense class="py-0">
                              <v-list-item v-for="c in move.result.choices" :key="c.regNum"
                                           :disabled="!c.ok || move.applying" @click="moveTo(stop, c)">
                                <v-list-item-content>
                                  <v-list-item-title>{{ c.regNum }}</v-list-item-title>
                                  <v-list-item-subtitle v-if="c.ok">
                                    <span :class="moveDeltaClass(c)">{{ moveDelta(c) }}</span>
                                    <v-chip v-for="x in moveExtras(c)" :key="x" x-small outlined
                                            color="deep-orange" class="ml-1">{{ x }}</v-chip>
                                  </v-list-item-subtitle>
                                  <v-list-item-subtitle v-if="c.ok">
                                    รถคันนี้กลับถึงบริษัท {{ hm(c.carReturn) }}
                                    · คันสุดท้ายกลับ {{ hm(c.latestReturn) }}
                                  </v-list-item-subtitle>
                                  <v-list-item-subtitle v-else class="red--text">รับไม่ได้: {{ c.message }}</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                              <v-list-item v-if="move.result.pinned" :disabled="move.applying" @click="unpinStop(stop)">
                                <v-list-item-content>
                                  <v-list-item-title>ยกเลิกการย้าย (ให้ระบบจัดเอง)</v-list-item-title>
                                  <v-list-item-subtitle>วางแผนใหม่ทั้งวัน</v-list-item-subtitle>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                    </td>
                  </tr>

                  <!-- The same three numbers the booking list shows, read the same
                       way: the SAP lines when the booking has them, the free text
                       when it does not. จำนวน and เวลาติดตั้ง are taken off the
                       PLANNED job rather than recomputed from the booking, so what
                       staff check here is the number the timetable above was built
                       on and not a second opinion about it. -->
                  <tr v-if="isStopOpen(route, i)" :key="'d' + i" class="detail-row">
                    <td :colspan="DETAIL_COLSPAN" class="py-3">
                      <div class="caption font-weight-medium">รายการสินค้า</div>
                      <template v-if="productRows(stop).length">
                        <div v-for="(p, k) in productRows(stop)" :key="'p' + k" class="caption">
                          • {{ productLabel(p) }} — {{ productQty(p) || 1 }} ชิ้น
                        </div>
                      </template>
                      <template v-else-if="productLines(stop).length">
                        <div v-for="(line, k) in productLines(stop)" :key="'l' + k" class="caption">
                          • {{ line }}
                        </div>
                      </template>
                      <div v-else class="caption grey--text">ไม่ได้ระบุ</div>

                      <div class="caption mt-2">
                        <strong>จำนวนชิ้น:</strong> {{ stop.job.items }} ชิ้น{{ itemNote(stop) }}
                        ·
                        <strong>เวลาติดตั้ง:</strong> {{ stop.installMinutes }} นาที{{ installNote(stop) ? ' (' + installNote(stop) + ')' : '' }}
                        ·
                        <strong>ติดตั้งเสร็จ:</strong> {{ hm(stop.startAt + stop.installMinutes) }}
                        · <strong>ออกจากจุดนี้:</strong> {{ hm(stop.departAt) }}
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <!-- Never silently dropped. A job the planner could not place is the one
             thing staff must see, so it gets its own card and a reason. -->
        <v-card v-if="plan.unassigned.length" class="mb-3">
          <v-toolbar dense flat color="red darken-2" dark>
            <v-toolbar-title class="subtitle-1">
              ยังจัดรถไม่ได้ {{ plan.unassigned.length }} งาน
            </v-toolbar-title>
          </v-toolbar>
          <v-list dense>
            <v-list-item v-for="(u, i) in plan.unassigned" :key="i" :class="{ 'dup-row': !!dupOf(bookingsOf(u)) }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ u.job.booking.name || u.job.booking.custName || u.job.id || '—' }}
                  <span v-if="bookingsOf(u).length > 1" class="caption ml-1">
                    ({{ bookingsOf(u).length }} การจอง)
                  </span>
                  <span class="caption grey--text ml-2">{{ placeOf(u) }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="red--text">{{ u.message }}</v-list-item-subtitle>
                <div v-if="dupOf(bookingsOf(u))">
                  <v-chip x-small color="red darken-1" dark>
                    <v-icon left x-small>mdi-content-duplicate</v-icon>{{ dupText(dupOf(bookingsOf(u))) }}
                  </v-chip>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Not a failure, which is why it is not red beside the card above.
             Far-province routes delivery booked deliberately: one route, one
             truck. The planner (dispatchPlan.planTrips) suggests a small car
             the plan above left free and orders the stops, so each route shows
             a car, an order and times -- or why it could not. Sending stays a
             person's act. Legs out here are timed at highway speed, with
             no Bangkok rush bands; the แผนที่ button gives Google's figure
             beside ours. -->
        <v-card v-if="plan.setAside && plan.setAside.length" class="mb-3">
          <v-toolbar dense flat color="deep-purple" dark>
            <v-toolbar-title class="subtitle-1">
              นอกพื้นที่ — จัดรถเอง {{ setAsideVisitCount }} จุด<template v-if="plan.setAside.length > setAsideVisitCount"> ({{ plan.setAside.length }} การจอง)</template>
            </v-toolbar-title>
          </v-toolbar>
          <!-- Grouped by route, and with the send button here rather than only
               on the board: THIS is the moment a delivery person learns the job
               needs dispatching by hand, and making them navigate to
               จองวันส่งสินค้า to act on it is a detour. Same
               dispatchService.sendTripToDispatch either way -- one code path,
               two doors into it. -->
          <div v-for="g in setAsideGroups" :key="g.tripId || 'none'">
            <v-divider />
            <div class="d-flex align-center flex-wrap px-4 pt-3">
              <v-chip v-for="p in g.provinces" :key="p" x-small color="deep-purple" dark class="mr-1">
                {{ p }}
              </v-chip>
              <span v-if="!g.tripId" class="caption error--text">ยังไม่มีเส้นทาง</span>
              <v-chip v-if="unpinnedOf(g)" x-small color="orange darken-3" dark class="ml-1">
                <v-icon left x-small>mdi-map-marker-off-outline</v-icon>ไม่มีพิกัด {{ unpinnedOf(g) }}
              </v-chip>
              <v-spacer />
              <!-- No route, nothing to send it on. Attaching it to one happens
                   on the board, which is where the routes are. -->
              <!-- Sent: the route holds its car (the planner keeps it out of
                   the Bangkok plan). ยกเลิกการส่ง takes back the jobs still
                   waiting on คิวรถเล็ก; a booking that joined after the send
                   still offers ส่งเข้าคิวจัดรถ, on the same car. -->
              <template v-if="sentOf(g)">
                <v-chip x-small color="green darken-1" dark class="mr-1">
                  <v-icon left x-small>mdi-truck-check</v-icon>
                  ส่งแล้ว · รถ {{ sentOf(g).regNum }} · {{ sentOf(g).count }}/{{ sentOf(g).total }} งาน
                </v-chip>
                <v-chip v-if="tripDaysOf(g) === 2" x-small color="indigo" text-color="white" class="mr-1">ค้างคืน</v-chip>
                <v-btn x-small text color="indigo" :loading="settingDays === g.tripId" @click="changeDays(g)">
                  แก้จำนวนวัน ({{ tripDaysOf(g) }} → {{ tripDaysOf(g) === 2 ? 1 : 2 }} วัน)
                </v-btn>
                <v-btn x-small text color="error" :loading="unsending === g.tripId" @click="unsend(g)">
                  <v-icon left small>mdi-undo</v-icon>ยกเลิกการส่ง
                </v-btn>
              </template>
              <v-btn v-if="g.tripId && (!sentOf(g) || sentOf(g).count < sentOf(g).total)"
                     x-small text color="deep-purple" :disabled="routeDispatched(g).length > 0" @click="openSend(g)">
                <v-icon left small>mdi-truck-check</v-icon>ส่งเข้าคิวจัดรถ
              </v-btn>
              <span v-if="!g.tripId" class="caption grey--text">เพิ่มเข้าเส้นทางที่หน้าจองวันส่งสินค้า</span>
            </div>
            <div v-if="routeDispatched(g).length" class="px-4 pt-1 caption error--text">
              SO ของเส้นทางนี้อยู่ในคิวรถเล็กแล้ว {{ routeDispatched(g).length }} งาน
              ({{ routeDispatched(g).map(c => c.regNum && c.regNum !== 'ไม่มีข้อมูล' ? c.regNum : 'ยังไม่ระบุรถ').filter((x, k, a) => a.indexOf(x) === k).join(', ') }})
              — ส่งเข้าคิวจัดรถไม่ได้จนกว่าจะลบงานนั้นที่หน้าคิวรถเล็ก
            </div>
            <template v-if="tripOf(g)">
              <div v-if="tripOf(g).ok" class="d-flex align-center flex-wrap px-4 pt-1 caption deep-purple--text">
                <span>{{ tripSummary(tripOf(g)) }}</span>
                <v-spacer />
                <v-btn x-small text color="deep-purple" @click="openRouteMap(tripOf(g))">
                  <v-icon left small>mdi-map</v-icon>แผนที่
                </v-btn>
              </div>
              <div v-else class="px-4 pt-1 caption warning--text text--darken-2">{{ tripProblem(g, tripOf(g)) }}</div>
            </template>
            <!-- The same three things a routed stop shows, read by the same
                 helpers: the written address, the ตำบล under it, and the
                 products behind a click. Delivery plans this round by hand, so
                 it needs exactly what the green table gives them -- it was
                 showing a name and a phone number and nothing to plan with.
                 The detail is hidden by default for the reason the green table
                 hides it: product lines are what a list stops being readable
                 as a list. -->
            <v-list dense>
              <!-- One line per visit: the same customer's SOs on this route are one
                   door (P.groupVisits, the routed stops' rule). -->
              <template v-for="(v, i) in visitsInOrder(g)">
                <v-list-item :key="v[0].id" :class="{ 'dup-row': !!dupOf(v) }">
                  <!-- Chevron then number then content then map, which is the
                       green table's column order. On a planned route the number
                       is the stop order; on one that could not be planned it is
                       only a row index. -->
                  <v-list-item-action class="mr-2 my-0">
                    <v-btn icon x-small @click="toggleAside(v[0])">
                      <v-icon small>
                        {{ isAsideOpen(v[0]) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                  <v-list-item-action class="mr-3 my-0 caption grey--text">{{ i + 1 }}</v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ bookingOf(v).name || bookingOf(v).custName || bookingOf(v).soNo || '—' }}
                      <span v-if="v.length === 1 && !RJ.isAppointment(v[0])" class="caption grey--text ml-2">{{ v[0].soNo ? 'SO ' : '' }}{{ RJ.refText(v[0]) }}</span>
                    </v-list-item-title>
                    <div v-if="v.length > 1" class="caption teal--text text--darken-2">
                      รวม {{ v.length }} การจอง: {{ v.map(b => RJ.refText(b)).join(', ') }}
                    </div>
                    <div v-if="addressOf(v)" class="caption">{{ addressOf(v) }}</div>
                    <div class="caption grey--text">{{ placeOf(v) }}</div>
                    <v-list-item-subtitle>
                      {{ slotLabelTh[v[0].slot] || v[0].slot }} · {{ v[0].telephone || v[0].custTel || '' }}
                      <span v-if="visitItems(v)"> · {{ visitItems(v) }} ชิ้น</span>
                    </v-list-item-subtitle>
                    <div v-if="tripStopOf(g, v)" class="caption deep-purple--text">
                      <template v-if="tripStopOf(g, v).day">วันที่ {{ tripStopOf(g, v).day }} · </template>ถึง {{ hm(tripStopOf(g, v).arriveAt) }} · ติดตั้ง {{ hm(tripStopOf(g, v).startAt) }}–{{ hm(tripStopOf(g, v).departAt) }}
                      <template v-if="tripStopOf(g, v).charge"> · {{ tripStopOf(g, v).charge.name }}</template>
                    </div>
                    <div v-if="dupOf(v)">
                      <v-chip x-small color="red darken-1" dark>
                        <v-icon left x-small>mdi-content-duplicate</v-icon>{{ dupText(dupOf(v)) }}
                      </v-chip>
                    </div>
                    <div v-if="!visitHasPin(v)">
                      <v-chip x-small color="orange darken-3" dark @click="openPin(v)">
                        <v-icon left x-small>mdi-map-marker-plus</v-icon>ไม่มีพิกัด
                      </v-chip>
                    </div>
                  </v-list-item-content>
                  <!-- Disabled rather than hidden when there is nothing to point
                       at, exactly as the routed rows do it: a button that
                       vanishes reads as a layout fault, one that greys out reads
                       as "this booking has no pin and no usable address". -->
                  <v-list-item-action class="my-0">
                    <v-btn icon x-small :disabled="!bookingMapUrl(bookingOf(v))" @click="openBookingMap(bookingOf(v))">
                      <v-icon small>mdi-map-marker</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="isAsideOpen(v[0])" :key="v[0].id + ':d'" class="detail-row">
                  <v-list-item-content class="py-2">
                    <div class="caption font-weight-medium">รายการสินค้า</div>
                    <template v-if="productRows(v).length">
                      <div v-for="(p, k) in productRows(v)" :key="'p' + k" class="caption">
                        • {{ productLabel(p) }} — {{ productQty(p) || 1 }} ชิ้น
                      </div>
                    </template>
                    <template v-else-if="productLines(v).length">
                      <div v-for="(line, k) in productLines(v)" :key="'l' + k" class="caption">
                        • {{ line }}
                      </div>
                    </template>
                    <div v-else class="caption grey--text">ไม่ได้ระบุ</div>
                    <div v-if="bookingOf(v).landmark" class="caption mt-1">
                      <strong>จุดสังเกต:</strong> {{ bookingOf(v).landmark }}
                    </div>
                    <div v-if="bookingOf(v).geo" class="caption"><strong>พิกัด:</strong> {{ bookingOf(v).geo }}</div>
                    <div>
                      <v-btn x-small text color="deep-purple" class="px-0" @click="openPin(v)">
                        <v-icon left x-small>mdi-pencil</v-icon>แก้ไขพิกัด / จุดสังเกต
                      </v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </div>
        </v-card>
      </template>

      <v-card v-else-if="!planning" class="pa-6 text-center grey--text">
        เลือกวันที่แล้วกด "วางแผน" เพื่อคำนวณการจัดรถ
      </v-card>

      <pin-dialog :visit="pinVisit" :date="date" @close="pinVisit = null" @saved="pinSaved" />

      <v-dialog v-model="send.open" max-width="420">
        <v-card>
          <v-toolbar color="deep-purple" dark dense flat>
            <v-toolbar-title class="subtitle-1">ส่งเข้าคิวจัดรถ</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div class="mb-3">{{ send.provinces }} · {{ date }} · {{ send.count }} งาน</div>
            <!-- The plates this plan already loaded. No second read of the
                 fleet, and no chance of offering a truck the plan does not
                 know about. -->
            <!-- A route already sent keeps its car: a late booking goes on the
                 same truck, never splitting one route across two. -->
            <v-select v-model="send.regNum" :items="planPlates" label="เลือกรถ"
                      color="deep-purple" hide-details="auto" @change="onSendCar"
                      :disabled="send.locked" :hint="send.locked ? 'เส้นทางนี้ส่งแล้วด้วยรถคันนี้' : ''"
                      :persistent-hint="send.locked" />
            <v-btn-toggle v-model="send.days" mandatory dense class="mt-2">
              <v-btn :value="1" small>1 วัน</v-btn>
              <v-btn :value="2" small>2 วัน (ค้างคืน)</v-btn>
            </v-btn-toggle>
            <!-- A two-day plan: the stops under each day, and ▲/▼ to move the
                 boundary one stop (dispatchPlan.retimeTripSplit, in the
                 worker). A move that cannot work is greyed with the reason.
                 Hidden once the route is sent: the page re-plans it on every
                 load, so the split here may not be the one that went out. -->
            <div v-if="send.route && send.route.days === 2 && send.days === 2 && !send.locked" class="mt-3">
              <div v-for="d in [1, 2]" :key="d" class="mb-1">
                <div class="caption font-weight-bold deep-purple--text">
                  วันที่ {{ d }} · {{ d === 1 ? 'ออก ' + hm(send.route.dayParts[0].startAt) + ' เสร็จ ' + hm(send.route.dayParts[0].endAt) : 'กลับ ' + hm(send.route.endAt) }}
                </div>
                <div v-for="(s, i) in send.route.stops.filter(x => x.day === d)" :key="d + '-' + i" class="caption">
                  {{ hm(s.startAt) }} {{ stopName(s) }}
                </div>
              </div>
              <div class="d-flex flex-wrap">
                <v-btn x-small text color="deep-purple" :disabled="send.timing || !splitOk(-1)" @click="moveSplit(-1)">
                  <v-icon x-small left>mdi-arrow-down</v-icon>ย้ายจุดสุดท้ายไปวันที่ 2
                </v-btn>
                <v-btn x-small text color="deep-purple" :disabled="send.timing || !splitOk(1)" @click="moveSplit(1)">
                  <v-icon x-small left>mdi-arrow-up</v-icon>ย้ายจุดแรกของวันที่ 2 มาวันที่ 1
                </v-btn>
              </div>
              <div v-for="dir in [-1, 1]" :key="'why' + dir">
                <div v-if="splitWhy(dir)" class="caption grey--text">
                  {{ dir === -1 ? 'ย้ายไปวันที่ 2 ไม่ได้' : 'ย้ายมาวันที่ 1 ไม่ได้' }}: {{ splitWhy(dir) }}
                </div>
              </div>
            </div>
            <div v-else-if="send.route && send.route.days === 2 && send.days === 1" class="caption orange--text text--darken-3 mt-2">
              แผนนี้ต้องใช้ 2 วัน — ส่งเป็น 1 วันได้ แต่จะไม่มีลำดับและเวลา
            </div>
            <div v-if="send.timing" class="caption grey--text mt-2">กำลังคำนวณเวลา…</div>
            <div v-else-if="send.note" class="caption deep-purple--text mt-2">{{ send.note }}</div>
            <v-alert dense text type="info" class="caption mt-3 mb-0">
              งานจะไปอยู่ในหน้าคิวรถเล็กของรถคันนี้ พร้อมพิกัดและจุดสังเกตที่บันทึกไว้
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="send.open = false">ยกเลิก</v-btn>
            <v-btn color="deep-purple" dark :disabled="!send.regNum || send.timing" :loading="send.saving"
                   @click="doSend">ส่ง</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmation shows the real write counts from a dry run, not a
           generic "are you sure". -->
      <v-dialog v-model="confirm.show" max-width="480" persistent>
        <v-card>
          <v-toolbar dense flat color="orange accent-3" dark>
            <v-toolbar-title class="subtitle-1">{{ confirm.title }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pt-4">
            <div v-if="confirm.loading" class="text-center py-4">
              <v-progress-circular indeterminate color="orange accent-3" />
            </div>
            <template v-else>
              <div v-for="(line, i) in confirm.lines" :key="i" class="body-2">{{ line }}</div>
              <v-alert v-if="confirm.manual" dense text type="info" class="mt-3 mb-0">
                นัดหมายที่จัดเอง {{ confirm.manual }} รายการ จะไม่ถูกแก้ไขหรือลบ
              </v-alert>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="confirm.show = false">ยกเลิก</v-btn>
            <v-btn :color="confirm.color" dark :disabled="confirm.loading" @click="confirm.go()">
              {{ confirm.ok }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </v-app>
</template>

<script>
import mainMenu from '@/components/mainMenu'
import proposalPanel from '@/components/proposalPanel'
import R from '@/components/bookingRules'
import D from '@/components/dispatchService'
import P from '@/components/dispatchPlan'
import planner from '@/components/plannerClient'
import pinDialog from '@/components/pinDialog'
import T from '@/components/travelTime'
import I from '@/components/installTime'
import X from '@/components/proposalRules'
import RJ from '@/components/returnJob'
import { reportError } from '@/components/report'

// Columns in the per-car timetable, which is what the expanded detail row has to
// span. Kept next to the header it counts so the two move together.
const DETAIL_COLSPAN = 11

// Where a stop's duration came from, said in the words the summary chips above
// the cards already use. 'explicit' -- counted off the SO or typed by staff --
// gets no note: it is the ordinary case, and labelling every ordinary row buries
// the three that are not.
const INSTALL_NOTE = {
  manual: { text: 'กำหนดเวลาเอง', color: 'green--text text--darken-2' },
  productList: { text: 'อ่านจากรายการ', color: 'amber--text text--darken-3' },
  default: { text: 'ค่าเริ่มต้น', color: 'red--text' },
  return: { text: 'รับคืน 30 นาที', color: 'blue--text text--darken-2' }
}

// The ITEM count's own provenance, which is not the same question: a stop can
// have a duration a person set over a count nobody confirmed.
const ITEM_NOTE = {
  productList: ' (อ่านจากรายการ)',
  default: ' (ค่าเริ่มต้น)'
}

// Thai for the flags dispatchPlan raises on a stop. Kept here rather than in the
// planner because they are display copy, and the planner stays pure.
const FLAG_TH = {
  EXPENSIVE: 'ค่าใช้จ่ายสูง',
  COARSE_LOCATION: 'ตำแหน่งไม่ละเอียด',
  LONG_WAIT: 'รอนาน',
  ITEMS_ESTIMATED: 'จำนวนชิ้นประมาณ',
  ITEMS_UNKNOWN: 'ไม่ทราบจำนวนชิ้น',
  PIN_MISMATCH: 'พิกัดไม่ตรงกับตำบล',
  NO_PIN: 'ไม่มีพิกัด',
  SAME_VISIT: 'รวมจุดส่งเดียวกัน',
  SPLIT_VISIT: 'ลูกค้าเดียวกัน คนละช่วง',
  MOVED: 'ย้ายรถด้วยมือ',
  MOVE_REFUSED: 'ย้ายรถไม่ได้'
}

// Nobody should have to punch a colon into a time field, and no keystroke that
// cannot belong in a clock should be accepted only to be rejected later. These
// two run on every keystroke and on blur respectively.
//
// A digit is dropped rather than corrected when it cannot stand where it was
// typed -- 7 in the tens of the minutes, 5 after a 2 in the hours. Silently
// rewriting it to something the user did not type is worse: they would not
// notice.
function maskClock (raw) {
  let d = String(raw == null ? '' : raw).replace(/\D/g, '')
  if (!d) return ''
  // 3-9 cannot start a two-digit hour, so it is a one-digit hour: 630 -> 06:30.
  if (d[0] > '2') d = '0' + d
  if (d.length >= 2 && Number(d.slice(0, 2)) > 23) d = d.slice(0, 1)
  if (d.length >= 3 && d[2] > '5') d = d.slice(0, 2)
  d = d.slice(0, 4)
  return d.length <= 2 ? d : d.slice(0, 2) + ':' + d.slice(2)
}

// On blur, a half-typed time is finished rather than left to fail validation:
// 6 is 06:00, 06:3 is 06:30. An empty field stays empty and runPlan says so.
function completeClock (raw) {
  const d = String(raw == null ? '' : raw).replace(/\D/g, '')
  if (!d) return ''
  const hh = d.length === 1 ? '0' + d : d.slice(0, 2)
  const rest = d.slice(2)
  const mm = rest.length === 0 ? '00' : (rest.length === 1 ? rest + '0' : rest)
  return hh + ':' + mm
}

const FLAG_COLOR = {
  EXPENSIVE: 'red darken-1',
  COARSE_LOCATION: 'grey darken-1',
  LONG_WAIT: 'blue-grey',
  ITEMS_ESTIMATED: 'amber darken-2',
  ITEMS_UNKNOWN: 'red darken-3',
  PIN_MISMATCH: 'deep-orange darken-2',
  NO_PIN: 'orange darken-3',
  SAME_VISIT: 'teal darken-1',
  SPLIT_VISIT: 'purple darken-1',
  MOVED: 'indigo darken-1',
  MOVE_REFUSED: 'red darken-2'
}

export default {
  name: 'DispatchPlan',
  components: { mainMenu, proposalPanel, pinDialog },

  data () {
    return {
      date: R.todayStr(),
      departAt: P.DEFAULTS.departAt,
      // Per car, and only where staff have overruled the computed time.
      departByCar: {},
      dateMenu: false,
      // Keyed "<plate>:<index>", so re-planning -- which rebuilds every route
      // object -- cannot leave a row open against a stop that has moved.
      openStops: {},
      // Keyed by booking id, not by position: the set-aside list re-sorts when a
      // route is sent, and an index would move the open row under the reader.
      openAside: {},
      send: { open: false, saving: false, timing: false, locked: false, tripId: '', regNum: '', days: 1, route: null, note: '', provinces: '', count: 0, split: null },
      sent: '',
      DETAIL_COLSPAN: DETAIL_COLSPAN,
      planning: false,
      committing: false,
      clearing: false,
      plan: null,
      // Both plans from one วางแผน (see dispatchPlan.planOptions); `plan` is
      // whichever is selected, so everything below reads one plan as before.
      plans: null,
      mode: 'minCharge',
      // Stops moved by hand, { <job id>: <plate> }. Kept across re-plans (a
      // departure edit, switching plans) until the date changes, so a
      // decision staff made is not undone by pressing วางแผน again.
      pins: {},
      // The ย้ายไปรถ menu that is open: which stop, and what each car would do.
      move: { key: '', loading: false, applying: false, result: null, error: '' },
      // The bookingSlot rows of the visit whose พิกัด is being edited, or null.
      pinVisit: null,
      // The tripId whose ยกเลิกการส่ง is running.
      unsending: '',
      settingDays: '',
      planProgress: { done: 0, total: 0 },
      error: '',
      assignedCount: 0,
      confirm: { show: false, loading: false, title: '', lines: [], ok: '', color: 'success', manual: 0, go: () => {} }
    }
  },

  computed: {
    // Propose and confirm: a date with a pending request moving off it, onto
    // it, or cancelling on it is decided before it is committed -- the planner
    // never guesses which of the two days a customer is on.
    // TRANSITION PERIOD -- see dispatchPlan.dispatchedConflicts.
    dispatched () { return (this.plan && this.plan.dispatched) || [] },
    dispatchedById () {
      const out = {}
      this.dispatched.forEach(c => { out[c.id] = c })
      return out
    },
    blockingProposals () {
      return X.proposalBlocksPlan(this.$store.state.proposals, this.date)
    },
    // Read straight from bookingRules rather than restated here: the จองวันส่งสินค้า
    // list and this card must call the same slot the same thing.
    slotLabelTh () { return R.SLOT_LABEL_TH },
    // Set-aside bookings gathered back into the routes they sit on, because
    // sending is a per-route act: one route, one truck, one plate. P.groupSetAside
    // is pure and asserted; this only hands it the plan's list.
    setAsideVisitCount () {
      return this.setAsideGroups.reduce((a, g) => a + g.visits.length, 0)
    },
    // planTrips' answer per route, for the plan on screen.
    tripPlans () {
      const out = {}
      ;((this.plan && this.plan.trips) || []).forEach(t => { out[t.tripId] = t })
      return out
    },
    setAsideGroups () {
      return P.groupSetAside((this.plan && this.plan.setAside) || [], b => R.provinceOf(b))
    },
    // Straight off the plan, which already loaded the fleet to compute itself.
    planPlates () { return ((this.plan && this.plan.cars) || []).map(c => c.regNum) },
    summaryTiles () {
      const s = this.plan.summary
      return [
        { label: 'รถที่ใช้', value: s.cars + ' คัน' },
        { label: 'จุดส่งทั้งหมด', value: s.bookings > s.jobs ? `${s.jobs} (${s.bookings} การจอง)` : s.jobs },
        { label: 'ยังจัดไม่ได้', value: s.unassigned },
        { label: 'ระยะทางรวม', value: s.km.toFixed(0) + ' กม.' },
        { label: 'ค่าเชื้อเพลิง', value: '฿' + s.fuelCost.toFixed(0) },
        { label: 'ค่าล่วงเวลา (ประมาณ)', value: '฿' + (s.overtimeCost || 0).toFixed(0) },
        { label: 'กลับถึงบริษัทช้าสุด', value: this.hm(s.latestReturn) },
        { label: 'ชาร์จรถไฟฟ้า', value: this.chargeText(s) },
        { label: 'ลดเวลารอ', value: (s.idleTrimmed || 0) + ' นาที' }
      ]
    },

    // The two cards of the comparison, built from each plan's own summary.
    planChoices () {
      if (!this.plans || !this.plans.minFuel) return []
      const card = (mode, title, hint) => {
        const s = this.plans[mode].summary
        const rows = [
          { label: 'ค่าเชื้อเพลิง', value: '฿' + s.fuelCost.toFixed(0) },
          // Estimated from the 08:00-17:00 day (dispatchPlan DEFAULTS), and
          // named as an estimate: it is what the planner weighed, not payroll.
          { label: 'ค่าล่วงเวลา (ประมาณ)', value: '฿' + s.overtimeCost.toFixed(0) },
          { label: 'ค่าใช้จ่ายรวม', value: '฿' + (s.fuelCost + s.overtimeCost).toFixed(0), strong: true },
          { label: 'รถที่ใช้', value: s.cars + ' คัน' },
          { label: 'ชาร์จรถไฟฟ้า', value: this.chargeText(s) },
          { label: 'กลับถึงบริษัทช้าสุด', value: this.hm(s.latestReturn) }
        ]
        if (this.plans.minCharge.summary.unassigned || this.plans.minFuel.summary.unassigned) {
          rows.push({ label: 'ยังจัดไม่ได้', value: s.unassigned })
        }
        return { mode: mode, title: title, hint: hint, rows: rows }
      }
      return [
        card('minCharge', 'ชาร์จน้อยที่สุด', 'ชาร์จเฉพาะเมื่อรถคันอื่นรับงานไม่ไหว'),
        card('minFuel', 'ประหยัดน้ำมัน', 'ยอมชาร์จระหว่างวันเพื่อลดค่าเชื้อเพลิง (เผื่อเวลาต่อคิวแล้ว)')
      ]
    },

    moveCount () { return Object.keys(this.pins).length },

    pinned () {
      const s = this.plan.summary
      return s.jobs - (s.noPin || 0)
    },

    items () {
      const s = this.plan.summary
      return {
        explicit: s.itemsCounted || 0,
        estimated: s.itemsEstimated || 0,
        assumed: s.itemsAssumed || 0,
        manual: s.itemsManual || 0
      }
    }
  },

  created () {
    this.RJ = RJ
    this.refreshAssigned()
  },

  // The mask has to run through departAt rather than off the input event: the
  // field must hold the rejected keystroke for a tick, or the bound value never
  // changes and Vuetify leaves the stray character sitting on screen.
  watch: {
    departAt (val) {
      const masked = maskClock(val)
      if (masked !== val) this.departAt = masked
    }
  },

  methods: {
    hm (minutes) { return T.toClock(minutes) },

    completeDepart () { this.departAt = completeClock(this.departAt) },

    // Emptying the field is how an override is removed: the car goes back to
    // the computed departure. Anything unreadable does the same, since the
    // planner drops what it cannot parse and the field re-renders from the plan.
    setDepart (route, value) {
      const plate = route.car.regNum
      const typed = completeClock(value)
      if (typed && typed !== this.hm(route.startAt)) this.$set(this.departByCar, plate, typed)
      else if (!typed) this.$delete(this.departByCar, plate)
      else return
      // Stay on the plan being looked at: the time was typed against it.
      this.runPlan({ keepMode: true })
    },

    // A planned stop wraps its booking in `job`; a set-aside booking IS the
    // booking, because loadDayBookings never routed it and so never built a job
    // around it. The four readers below serve both rather than growing a second
    // copy each -- two readers of the same field that can disagree is the shape
    // of bug this codebase has already paid for twice.
    //
    // A set-aside visit is an array of bookings (P.groupVisits); it reads as its
    // pinned member, else its first -- the rule a merged routed stop follows.
    bookingOf (entry) {
      const e = entry || {}
      if (Array.isArray(e)) return e.find(b => b && b.geo) || e[0] || {}
      return (e.job && e.job.booking) || e
    },

    // Every booking an entry delivers: a merged stop's members, else the one.
    bookingsOf (entry) {
      const e = entry || {}
      if (Array.isArray(e)) return e
      if (e.job && e.job.bookings && e.job.bookings.length) return e.job.bookings
      return [this.bookingOf(e)]
    },

    visitItems (v) {
      return (v || []).reduce((a, b) => a + (Number(b.itemCount) || 0), 0)
    },

    addressOf (entry) {
      const b = this.bookingOf(entry)
      return (b.address || b.custAddress || '').trim()
    },

    stopKey (route, i) { return route.car.regNum + ':' + i },

    isStopOpen (route, i) { return !!this.openStops[this.stopKey(route, i)] },

    // Same builder the routed rows use -- P.mapUrlForBooking is what
    // mapUrlForStop now delegates to, so the two cannot point anywhere
    // different for the same customer.
    bookingMapUrl (b) { return P.mapUrlForBooking(b) },
    openBookingMap (b) {
      const url = P.mapUrlForBooking(b)
      if (url) window.open(url, '_blank', 'noopener')
    },

    isAsideOpen (b) { return !!this.openAside[b.id] },
    toggleAside (b) {
      if (this.openAside[b.id]) this.$delete(this.openAside, b.id)
      else this.$set(this.openAside, b.id, true)
    },

    toggleStop (route, i) {
      const key = this.stopKey(route, i)
      if (this.openStops[key]) this.$delete(this.openStops, key)
      else this.$set(this.openStops, key, true)
    },

    // The SAP lines when the booking has them. `prodList` is what bookingDate
    // stores off GetProdList, and it is the only breakdown that names products;
    // productList below is the free text a นัดหมาย carries instead.
    // Across every booking of the stop, so a merged visit lists all it carries.
    productRows (stop) {
      return [].concat.apply([], this.bookingsOf(stop)
        .map(b => (Array.isArray(b.prodList) ? b.prodList : [])))
    },

    productLines (stop) {
      return [].concat.apply([], this.bookingsOf(stop).map(b => String(b.productList || '')
        .split(/[\n\r]+/).map(l => l.trim()).filter(Boolean)))
    },

    productLabel (row) { return I.productLabel(row) },
    productQty (row) { return I.productQty(row) },

    itemNote (stop) { return ITEM_NOTE[stop.job.itemSource] || '' },

    installNote (stop) {
      const note = INSTALL_NOTE[stop.job.installSource]
      return note ? note.text : ''
    },

    installNoteColor (stop) {
      const note = INSTALL_NOTE[stop.job.installSource]
      return note ? note.color : 'grey--text'
    },

    // "ชาร์จ +23′" said neither when it happens nor whether anyone knows where.
    // Both matter: the charge is taken AFTER this stop's installation, before
    // the next leg, and a round with no known station on it is one where the
    // driver has to find a public charger themselves -- which is what the extra
    // minutes are for, not the charging.
    chargeLabel (c) {
      if (c.kind === 'refuel') return `${c.name}หลังงานนี้ +${c.addedMinutes}′`
      const where = (c.source === 'known' ? `ที่ ${c.name}` : '(หาสถานีเอง)') +
        // Over lunch the half hour of charging IS the meal, so it is not in the
        // +minutes -- which then read as too short for a charge.
        (c.absorbed ? ' ระหว่างพักเที่ยง' : '')
      // The queue allowance is named, so a driver who finds the bay free knows
      // why the plan has them early for the next customer.
      const queue = c.queueMinutes ? ` (เผื่อคิว ${c.queueMinutes}′)` : ''
      return `ชาร์จหลังงานนี้ ${where} +${c.addedMinutes}′${queue}`
    },

    // What happens between the end of the work and the car leaving, when
    // anything does: "ชาร์จ + พักเที่ยง · ออก 12:57".
    breakNote (route, stop) {
      const done = stop.startAt + stop.installMinutes
      if (stop.departAt - done < 0.5) return ''
      const parts = []
      if (stop.charge) parts.push(stop.charge.kind === 'refuel' ? stop.charge.name : 'ชาร์จ')
      if (route.lunchAt != null && route.lunchAt >= done - 1e-9 && route.lunchAt < stop.departAt) parts.push('พักเที่ยง')
      return (parts.length ? parts.join(' + ') + ' · ' : '') + 'ออก ' + this.hm(stop.departAt)
    },

    placeOf (entry) {
      const b = this.bookingOf(entry)
      const loc = R.parseLocation(b.location)
      return loc ? R.locationLabel(loc) : (b.address || '')
    },

    // Opens the whole round in Google Maps: depot, every stop in planned order,
    // depot again. Free, no API key, and it opens in the driver's Maps app.
    //
    // It is also a second opinion on this planner's own distances, which are
    // haversine x 1.35 and worth about +/-15%. A round whose Maps total differs
    // wildly from the plan is worth a look.
    openRouteMap (route) {
      const m = P.mapUrlForRoute(route)
      if (!m) {
        return this.$root.$message('เปิดแผนที่ไม่ได้',
          'งานในรอบนี้ไม่มีที่อยู่หรือพิกัดที่ใช้นำทางได้',
          { type: 'alert', color: 'warning', width: 400 })
      }
      // Google's URL API takes at most nine waypoints. No round here comes
      // close, but silently dropping the last stops is the kind of thing a
      // driver would discover at 16:00.
      if (m.truncated) {
        this.$root.$message('แผนที่แสดงได้ไม่ครบ',
          [`Google Maps รับได้สูงสุด ${P.MAX_WAYPOINTS} จุดต่อเส้นทาง`,
            `รอบนี้มี ${route.stops.length} จุด จะไม่แสดง ${m.dropped} จุดสุดท้าย`],
          { type: 'alert', color: 'warning', width: 430 })
      }
      window.open(m.url, '_blank', 'noopener')
    },

    stopMapUrl (stop) { return P.mapUrlForStop(stop) },

    openStopMap (stop) {
      const url = P.mapUrlForStop(stop)
      if (url) window.open(url, '_blank', 'noopener')
    },

    flagLabel (f) { return FLAG_TH[f] || f },
    flagColor (f) { return FLAG_COLOR[f] || 'grey' },

    // Range for every vehicle, not just the EVs, and read from `rangeKm` rather
    // than from whatever the label happens to say -- the planner decides on the
    // number, so the screen should show that same number.
    carLabel (car) {
      const base = car.label || car.fuelType || ''
      if (!isFinite(car.rangeKm) || !car.rangeKm) return base
      const per = car.fuelType === 'ev' ? 'ชาร์จ' : 'ถัง'
      return `${base} · วิ่งได้ ${car.rangeKm} กม./${per}`
    },

    fuelColor (fuel) {
      if (fuel === 'ev') return 'green darken-2'
      if (fuel === 'ngv') return 'blue darken-2'
      return 'brown darken-2'
    },

    // A charge taken over lunch is named, because it is the least risky kind:
    // the crew is stopped anyway, and a queue eats lunch before it reaches the
    // next customer.
    chargeText (s) {
      if (!s.charges) return 'ไม่ต้องชาร์จ'
      const lunch = s.chargesAtLunch
        ? (s.chargesAtLunch === s.charges ? ' (ช่วงพักเที่ยง)' : ` (พักเที่ยง ${s.chargesAtLunch})`)
        : ''
      return `${s.charges} ครั้ง${lunch}` + (s.chargingNeeded ? ' — จำเป็น' : '')
    },

    planConfig () {
      return { departAt: this.departAt, departByCar: this.departByCar, pins: this.pins }
    },

    // Only what the planner needs of a plan: everything sent to the worker
    // is copied, and the bookings and the other plan are not needed.
    slimPlan (p) {
      return {
        routes: p.routes,
        summary: p.summary,
        config: p.config,
        cars: p.cars,
        unassigned: p.unassigned,
        mode: p.mode,
        // So ย้ายไปรถ re-plans the out-of-area routes, and tripForCar finds them.
        setAside: p.setAside,
        trips: p.trips
      }
    },

    // ย้ายไปรถ: each car is the day with just this stop moved there
    // (dispatchPlan.moveChoices) -- quick, since only two cars change. Worked
    // out in the planner's worker; a menu closed or reopened meanwhile
    // ignores an answer it no longer wants.
    openMove (route, i, stop) {
      const key = this.stopKey(route, i)
      this.move = { key: key, loading: true, applying: false, result: null, error: '' }
      const still = () => this.move.key === key
      planner.moveMenu(this.slimPlan(this.plan), stop.job.id).then(result => {
        if (!still()) return
        // Frozen like the plans: each choice carries a whole day, only read.
        this.move.result = Object.freeze(result)
        this.move.loading = false
      }).catch(e => {
        if (!still()) return
        this.move.error = 'คำนวณไม่สำเร็จ: ' + (e && e.message ? e.message : e)
        this.move.loading = false
      })
    },
    closeMove (route, i, open) {
      if (!open && this.move.key === this.stopKey(route, i)) this.move.key = ''
    },
    // Applies exactly the plan the menu showed -- no re-plan, so what was
    // priced is what is on screen. The stop is pinned, so a later วางแผน keeps
    // it on this car. The other plan gets the same move where it can take it,
    // so switching plans does not quietly undo it; where it cannot, the pin
    // still holds at the next วางแผน, which says so if the car refuses.
    async moveTo (stop, choice) {
      const jobId = stop.job.id
      this.move.applying = true
      try {
        const other = this.mode === 'minFuel' ? 'minCharge' : 'minFuel'
        const otherPlan = this.plans[other]
        let otherMoved = null
        if (otherPlan) {
          const r = await planner.moveLocal(this.slimPlan(otherPlan), jobId, choice.regNum)
          otherMoved = r && r.ok ? r.plan : null
        }
        this.$set(this.pins, jobId, choice.regNum)
        const pinsNow = Object.assign({}, this.pins)
        const withPins = (p, moved) => Object.assign({}, p, moved,
          { config: Object.assign({}, (moved || p).config, { pins: pinsNow }) })
        const current = withPins(this.plan, choice.plan)
        const second = otherPlan ? withPins(otherPlan, otherMoved) : null
        P.pairPlans(current, second)
        const plans = {}
        plans[this.mode] = Object.freeze(current)
        plans[other] = second ? Object.freeze(second) : null
        this.plans = plans
        this.plan = plans[this.mode]
        this.openStops = {}
        this.move = { key: '', loading: false, applying: false, result: null, error: '' }
      } catch (e) {
        this.move.applying = false
        this.move.error = 'ย้ายไม่สำเร็จ: ' + (e && e.message ? e.message : e)
      }
    },
    // Giving a stop back to the planner cannot be done locally -- where it
    // would go is a whole-day question -- so it re-plans.
    unpinStop (stop) {
      this.$delete(this.pins, stop.job.id)
      this.move = { key: '', loading: false, applying: false, result: null, error: '' }
      this.runPlan({ keepMode: true })
    },
    clearMoves () {
      this.pins = {}
      this.runPlan({ keepMode: true })
    },
    // Fuel + overtime for the whole day, against the plan on screen.
    moveDelta (c) {
      const d = Math.round(c.delta)
      return d === 0 ? 'ค่าใช้จ่ายเท่าเดิม' : (d > 0 ? `แพงขึ้น ฿${d}` : `ถูกลง ฿${-d}`)
    },
    moveDeltaClass (c) {
      const d = Math.round(c.delta)
      return d < 0 ? 'green--text text--darken-2 font-weight-medium' : d > 0 ? 'red--text text--darken-1' : ''
    },
    // What the baht do not show: an extra car and crew, or the spare.
    moveExtras (c) {
      const out = []
      if (c.spareAdded) out.push('ใช้รถสำรอง')
      if (c.carsDelta > 0) out.push(`เพิ่มรถ ${c.carsDelta} คัน`)
      if (c.carsDelta < 0) out.push(`ลดรถ ${-c.carsDelta} คัน`)
      return out
    },
    flagText (stop, f) {
      if (f === 'MOVE_REFUSED' && stop.job.pin) {
        return `ย้ายไป ${stop.job.pin.regNum} ไม่ได้: ${P.PIN_REASON_TH[stop.job.pin.reason] || ''}`
      }
      return this.flagLabel(f)
    },

    selectPlan (mode) {
      if (!this.plans || !this.plans[mode]) return
      this.mode = mode
      this.plan = this.plans[mode]
      // Stop indexes mean different customers in the other plan.
      this.openStops = {}
    },

    reset () {
      this.plan = null
      this.plans = null
      this.pins = {}
      this.mode = 'minCharge'
      this.departByCar = {}
      this.openStops = {}
      this.error = ''
      this.refreshAssigned()
    },

    async refreshAssigned () {
      try {
        const rows = await D.loadAutoAssigned(this.date)
        this.assignedCount = rows.length
      } catch (e) {
        this.assignedCount = 0
      }
    },

    tripOf (g) { return g.tripId ? this.tripPlans[g.tripId] || null : null },
    // The conflict of a row's bookings (a merged stop has several), or null.
    dupOf (bookings) {
      return (bookings || []).map(b => b && this.dispatchedById[b.id]).find(Boolean) || null
    },
    dupText (c) {
      return 'อยู่ในคิวรถเล็ก · ' + (c.regNum && c.regNum !== 'ไม่มีข้อมูล' ? 'รถ ' + c.regNum : 'ยังไม่ระบุรถ')
    },
    routeDispatched (g) {
      const ids = g.rows.map(b => b.id)
      return this.dispatched.filter(c => ids.indexOf(c.id) !== -1)
    },
    // ALREADY_DISPATCHED from a writer: an SO went onto คิวรถเล็ก after วางแผน.
    dispatchedLines (e) {
      return ['มี SO ที่อยู่ในคิวรถเล็กแล้ว จึงไม่ได้บันทึก เพื่อไม่ให้เกิดงานซ้ำ',
        ...((e && e.conflicts) || []).map(c => `• ${c.soNo ? 'SO ' + c.soNo : 'RT ' + c.rtNo} ${c.name}`),
        'ลบงานนั้นที่หน้าคิวรถเล็ก หรือจัดรถวันนี้ด้วยมือต่อไป แล้วกดวางแผนใหม่']
    },
    sentOf (g) { const t = this.tripOf(g); return t && t.sent ? t.sent : null },
    // ยกเลิกการส่ง: confirmed first, then dispatchService.unsendTrip, which
    // refuses a route with a job already confirmed on คิวรถเล็ก.
    async unsend (g) {
      const sent = this.sentOf(g)
      const yes = await this.$root.$message('ยกเลิกการส่งเข้าคิวจัดรถ', [
        `เส้นทาง ${g.provinces.join(', ')} · รถ ${sent.regNum}`,
        'งานของเส้นทางนี้ที่ยังรออยู่ในคิวรถเล็กจะถูกลบ และเส้นทางจะกลับมาวางแผนและส่งใหม่ได้',
        'ต้องการดำเนินการต่อหรือไม่?'
      ], { type: 'confirm', color: 'warning', width: 460 })
      if (!yes) return
      this.unsending = g.tripId
      try {
        const r = await D.unsendTrip(g.tripId, { user: this.$store.state.user || '' })
        this.$root.$message('ยกเลิกการส่งแล้ว', `ลบงานออกจากคิวรถเล็ก ${r.deleted} งาน`,
          { type: 'alert', color: 'success', width: 400 })
        await this.runPlan({ keepMode: true })
      } catch (e) {
        const lines = e && e.message === 'UNSEND_BLOCKED'
          ? ['มีงานที่ยืนยันการจัดรถในคิวรถเล็กแล้ว จึงยกเลิกทั้งเส้นทางจากหน้านี้ไม่ได้',
            ...(e.blocked || []).map(b => `• ${b.name || '-'}${' ' + RJ.refText(b)} — ${b.why === 'CONFIRMED' ? 'ยืนยันแล้ว' : 'ไม่พบงานในคิวรถเล็ก ตรวจสอบก่อน'}`),
            'โปรดลบงานนั้นที่หน้าคิวรถเล็กก่อน แล้วกดยกเลิกการส่งอีกครั้ง']
          : [{ NO_TRIP: 'ไม่พบเส้นทางนี้', NOT_SENT: 'เส้นทางนี้ยังไม่ได้ส่งเข้าคิวจัดรถ' }[e && e.message] || 'เกิดข้อผิดพลาด โปรดลองใหม่']
        this.$root.$message('ยกเลิกการส่งไม่สำเร็จ', lines, { type: 'alert', color: 'error', width: 480 })
      } finally {
        this.unsending = ''
      }
    },
    // The route's days as its bookings carry them (tripDays, stamped on send).
    tripDaysOf (g) {
      return (g.rows || []).some(b => b.tripDays === 2) ? 2 : 1
    },
    // แก้จำนวนวัน: flips 1 <-> 2 on a sent route, then plans the day again as
    // ยกเลิกการส่ง does.
    async changeDays (g) {
      const days = this.tripDaysOf(g) === 2 ? 1 : 2
      this.settingDays = g.tripId
      try {
        const r = await D.setTripDays(g.tripId, { days, user: this.$store.state.user || '' })
        this.$root.$message('แก้จำนวนวันแล้ว', [`${days} วัน · งานในคิว ${r.jobs} · งานที่จัดรถแล้ว ${r.rows}`].concat(R.overDayLines(r.overDay)),
          { type: 'alert', color: r.overDay ? 'warning' : 'success', width: 440 })
        await this.runPlan({ keepMode: true })
      } catch (e) {
        reportError('setTripDays', e)
        const why = { BAD_DAYS: 'จำนวนวันไม่ถูกต้อง', NO_TRIP: 'ไม่พบเส้นทางนี้', NOT_SENT: 'เส้นทางนี้ยังไม่ได้ส่งเข้าคิวจัดรถ' }[e && e.message]
        this.$root.$message('แก้จำนวนวันไม่สำเร็จ', why || String((e && e.message) || e), { type: 'alert', color: 'error', width: 440 })
      } finally {
        this.settingDays = ''
      }
    },
    visitHasPin (v) { return P.visitPinned(v) },
    unpinnedOf (g) { return g.visits.filter(v => !P.visitPinned(v)).length },
    openPin (bookings) { this.pinVisit = (bookings || []).slice() },
    // Saved: the stop's point, the order and the map all change with a pin,
    // so the day is planned again -- keeping the mode and every ย้ายไปรถ pin.
    pinSaved () {
      this.pinVisit = null
      this.runPlan({ keepMode: true })
    },
    // The route's visits in planned order when it was planned; as listed otherwise.
    visitsInOrder (g) {
      const t = this.tripOf(g)
      if (!t || !t.ok) return g.visits
      const rank = {}
      t.stops.forEach((s, i) => (s.job.bookings || [s.job.booking]).forEach(b => { rank[b.id] = i }))
      const at = v => rank[v[0].id] == null ? 99 : rank[v[0].id]
      return g.visits.slice().sort((a, b) => at(a) - at(b))
    },
    tripStopOf (g, v) {
      const t = this.tripOf(g)
      if (!t || !t.ok) return null
      return t.stops.find(s => (s.job.bookings || [s.job.booking]).some(b => b.id === v[0].id)) || null
    },
    tripSummary (t) {
      const refuel = (t.charges || []).map(c => c.name).join(', ')
      const tail = `${t.km.toFixed(0)} กม. · ฿${t.fuelCost.toFixed(0)}` + (refuel ? ` · ${refuel}` : '')
      if (t.days === 2) {
        return `รถ ${t.car.regNum} · 2 วัน (ค้างคืน) · ออก ${this.hm(t.startAt)} · ` +
          `วันที่ 1 เสร็จ ${this.hm(t.dayParts[0].endAt)} · วันที่ 2 กลับ ${this.hm(t.endAt)} · ` + tail
      }
      return `รถ ${t.car.regNum} · ออก ${this.hm(t.startAt)} กลับ ${this.hm(t.endAt)} · ` + tail
    },
    tripProblem (g, t) {
      if (t.reason === 'UNPLACEABLE') {
        const names = g.rows.filter(b => t.unplaced.indexOf(b.id) !== -1).map(b => b.name || b.custName || b.soNo || b.id)
        return `ยังวางแผนไม่ได้ — ${t.message}: ${names.join(', ')}`
      }
      if (t.reason === 'NO_FREE_CAR') {
        const where = t.wouldFit.usedBy === 'route' ? 'ใช้กับเส้นทางนอกพื้นที่อื่นแล้ว' : 'ใช้ในแผนในเมืองแล้ว'
        const reach = t.wouldFit.days === 2 ? `วิ่งถึงใน 2 วัน (กลับวันที่ 2 ${this.hm(t.wouldFit.endAt)})` : `วิ่งถึง (กลับ ${this.hm(t.wouldFit.endAt)})`
        return `${t.message} — ${t.wouldFit.regNum} ${reach} แต่${where}`
      }
      return `ยังวางแผนไม่ได้ — ${t.message}`
    },

    openSend (g) {
      const t = this.tripOf(g)
      const planned = t && t.ok ? t : null
      this.send = {
        open: true,
        saving: false,
        timing: false,
        tripId: g.tripId,
        regNum: planned ? planned.car.regNum : (t && t.sent ? t.sent.regNum : ''),
        locked: !!(t && t.sent),
        // A route already sent keeps its days: a different value would change
        // the route while earlier jobs keep their old tripEnd (แก้จำนวนวัน does
        // that). A new send of a two-day plan starts at 2.
        days: t && t.sent ? this.tripDaysOf(g) : (planned && planned.days === 2 ? 2 : this.tripDaysOf(g)),
        route: planned,
        note: planned ? 'แผนแนะนำ: ' + this.tripSummary(planned) : '',
        provinces: g.provinces.join(', '),
        count: g.rows.length,
        split: null
      }
      this.loadSplitChoices()
    },
    // Another plate than the suggestion: time the route on it, so what is sent
    // is that car's order and ETAs -- or none, said plainly, if it cannot go.
    async onSendCar (regNum) {
      const t = this.tripPlans[this.send.tripId]
      if (t && t.ok && t.car.regNum === regNum) {
        // Back to the suggestion while another car was being timed: that
        // answer is discarded when it lands, so the wait ends here.
        this.send.timing = false
        this.send.route = t
        this.send.note = 'แผนแนะนำ: ' + this.tripSummary(t)
        this.loadSplitChoices()
        return
      }
      this.send.route = null
      this.send.note = ''
      this.send.timing = true
      try {
        const r = await planner.tripForCar(this.slimPlan(this.plan), this.send.tripId, regNum)
        if (this.send.regNum !== regNum) return
        this.send.route = r && r.ok ? r : null
        if (r && r.ok && r.days === 2 && !this.send.locked) this.send.days = 2
        this.loadSplitChoices()
        this.send.note = r && r.ok
          ? this.tripSummary(r)
          : r && r.reason === 'CAR_BUSY'
            ? `${r.message} — ส่งได้ แต่จะไม่มีลำดับและเวลา`
            : `รถคันนี้ไปไม่ได้ตามแผน: ${(r && r.message) || ''} — ส่งได้ แต่จะไม่มีลำดับและเวลา`
      } catch (e) {
        if (this.send.regNum === regNum) this.send.note = 'คำนวณเวลาไม่สำเร็จ — ส่งได้ แต่จะไม่มีลำดับและเวลา'
      } finally {
        if (this.send.regNum === regNum) this.send.timing = false
      }
    },
    // What a send carries: a two-day plan sent as one day goes without times
    // (dispatchPlan.tripStopFields refuses it too).
    sendRoute () {
      const r = this.send.route
      return r && r.days === 2 && this.send.days !== 2 ? null : r
    },
    stopName (s) {
      const b = (s.job && (s.job.booking || (s.job.bookings || [])[0])) || {}
      return b.name || b.custName || b.soNo || (s.job && s.job.id) || ''
    },
    // The two neighbouring boundaries, worked out ahead so a move that cannot
    // work is greyed with its reason instead of failing on the click.
    async loadSplitChoices () {
      const r = this.send.route
      this.send.split = null
      if (!r || r.days !== 2) return
      const at = r
      try {
        const plan = this.slimPlan(this.plan)
        const [less, more] = await Promise.all([
          planner.retimeTripSplit(plan, r, r.split - 1).catch(() => null),
          planner.retimeTripSplit(plan, r, r.split + 1).catch(() => null)
        ])
        if (this.send.route !== at) return
        this.send.split = { '-1': less, '1': more }
      } catch (e) {
        if (this.send.route === at) this.send.split = null
        reportError('loadSplitChoices', e)
      }
    },
    splitOk (dir) {
      const c = this.send.split && this.send.split[String(dir)]
      return !!(c && c.ok)
    },
    splitWhy (dir) {
      const c = this.send.split && this.send.split[String(dir)]
      return c && !c.ok ? c.message || c.reason : ''
    },
    moveSplit (dir) {
      const c = this.send.split && this.send.split[String(dir)]
      if (!c || !c.ok) return
      this.send.route = c
      this.send.note = 'แผนที่ปรับวัน: ' + this.tripSummary(c)
      this.loadSplitChoices()
    },
    async doSend () {
      this.send.saving = true
      const sent = this.sendRoute()
      const planned = !!sent && !!sent.car && sent.car.regNum === this.send.regNum
      try {
        const r = await D.sendTripToDispatch(this.send.tripId,
          { regNum: this.send.regNum, user: this.$store.state.user || '', route: this.sendRoute(), days: this.send.days })
        this.send.open = false
        // Both numbers: "sent 0, skipped 3" is the answer to "why did nothing
        // happen", and a bare success would not give it.
        //
        // คิวรถเล็ก and not จัดรถ: this wrote `booking` documents, and จัดรถ
        // edits `deliveryList` rows, which do not exist yet. carBooking is the
        // single gate between the two -- its confirm step is what creates the
        // deliveryList / deliveryTimeline pair.
        const skipped = r.skipped ? ` (มีอยู่ในคิวแล้ว ${r.skipped} งาน)` : ''
        this.$root.$message(
          r.created ? 'ส่งเข้าคิวจัดรถแล้ว' : 'ไม่มีงานใหม่ที่ต้องส่ง',
          [r.created
            ? `ส่ง ${r.created} งานเข้าคิวของรถ ${r.regNum} แล้ว${planned ? ' พร้อมลำดับและเวลาโดยประมาณ' : ''}${skipped} — ยืนยันการจัดรถและจัดลำดับการส่งได้ที่ คิวรถเล็ก`
            : `งานของเส้นทางนี้อยู่ในคิวจัดรถแล้วทั้งหมด${skipped}`].concat(R.overDayLines(r.overDay)),
          { type: 'alert', color: r.overDay ? 'warning' : (r.created ? 'success' : 'info'), width: 480 })
        // The route now holds its car: plan again so the card says ส่งแล้ว
        // and the Bangkok plan is made without that car.
        if (r.created) this.runPlan({ keepMode: true })
      } catch (e) {
        const why = {
          NO_CAR: 'ยังไม่ได้เลือกรถ',
          NO_TRIP: 'ไม่พบเส้นทางนี้',
          TRIP_CANCELLED: 'เส้นทางนี้ถูกยกเลิกแล้ว',
          TRIP_EMPTY: 'เส้นทางนี้ยังไม่มีการจอง'
        }[e && e.message]
        const dup = e && e.message === 'ALREADY_DISPATCHED'
        this.$root.$message('ส่งเข้าคิวจัดรถไม่สำเร็จ', dup ? this.dispatchedLines(e) : (why || 'เกิดข้อผิดพลาด โปรดลองใหม่'),
          { type: 'alert', color: 'error', width: 460 })
        if (dup) { this.send.open = false; this.runPlan({ keepMode: true }) }
      } finally {
        this.send.saving = false
      }
    },

    async runPlan (opts) {
      const keep = opts && opts.keepMode ? this.mode : 'minCharge'
      this.planning = true
      this.planProgress = { done: 0, total: 0 }
      this.error = ''
      this.plan = null
      this.plans = null
      this.mode = 'minCharge'
      // A re-plan reorders stops, so an index that was open before it is a
      // different customer after it.
      this.openStops = {}
      try {
        if (T.toMinutes(this.departAt) == null) {
          this.error = 'เวลาออกจากคลังไม่ถูกต้อง ใช้รูปแบบ HH:MM เช่น 06:30'
          return
        }
        const planned = await D.planDay(this.date, {
          config: this.planConfig(),
          onProgress: p => { this.planProgress = p }
        })
        // Frozen: the page only reads a plan, and replaces it whole when it
        // changes, so there is nothing for Vue to watch inside it.
        const alternative = planned.alternative ? Object.freeze(planned.alternative) : null
        Object.freeze(planned)
        this.plans = { minCharge: planned, minFuel: alternative }
        this.plan = planned
        if (keep === 'minFuel' && this.plans.minFuel) this.selectPlan('minFuel')
        if (!this.plan.summary.jobs && !this.plan.unassigned.length) {
          // A day whose only bookings are out-of-area produces an empty plan
          // and is not an empty day. Saying so would send staff looking for
          // bookings the list above is already showing them.
          this.error = this.plan.setAside && this.plan.setAside.length
            ? ''
            : 'ไม่มีรายการจองในวันที่เลือก'
        }
        await this.refreshAssigned()
      } catch (e) {
        this.error = 'คำนวณแผนไม่สำเร็จ: ' + (e && e.message ? e.message : e)
      } finally {
        this.planning = false
      }
    },

    // The dialog reports what a dry run says will actually happen, rather than
    // asking "are you sure" about an unspecified amount of change.
    async askCommit () {
      if (this.blockingProposals.length) return
      this.confirm = {
        show: true, loading: true, title: 'ยืนยันแผนการจัดรถ', lines: [],
        ok: 'ยืนยันและบันทึก', color: 'success', manual: 0, go: this.doCommit
      }
      try {
        const [dry, manual] = await Promise.all([
          D.commitPlan(this.date, this.plan, { dryRun: true, user: this.$store.state.user || '' }),
          D.countManualBookings(this.date)
        ])
        this.confirm.lines = [
          `สร้างนัดหมายใหม่ ${dry.created} รายการ`,
          `อัปเดตนัดหมายเดิม ${dry.updated} รายการ`,
          `ลบนัดหมายที่ระบบเคยจัด ${dry.removed} รายการ`,
          `บันทึกทะเบียนรถกลับไปที่การจอง ${dry.slotsPatched} รายการ`
        ]
        // Stated as a number rather than as a reassurance: this is exactly what
        // commitPlan will not touch.
        this.confirm.manual = manual
        if (this.moveCount) this.confirm.lines.push(`ย้ายรถด้วยมือ ${this.moveCount} จุด`)
        if (this.plans && this.plans.minFuel) {
          this.confirm.lines.unshift(`แผนที่เลือก: ${this.mode === 'minFuel' ? 'ประหยัดน้ำมัน' : 'ชาร์จน้อยที่สุด'}`)
        }
        if (this.items.assumed) {
          this.confirm.lines.push('')
          this.confirm.lines.push(`หมายเหตุ: มี ${this.items.assumed} งานที่ไม่ทราบจำนวนชิ้น เวลาติดตั้งเป็นการประมาณ`)
        }
      } catch (e) {
        this.confirm.lines = e && e.message === 'ALREADY_DISPATCHED' ? this.dispatchedLines(e) : ['ตรวจสอบไม่สำเร็จ: ' + (e && e.message ? e.message : e)]
      } finally {
        this.confirm.loading = false
      }
    },

    async doCommit () {
      if (this.blockingProposals.length) return
      this.confirm.show = false
      this.committing = true
      try {
        const res = await D.commitPlan(this.date, this.plan, { user: this.$store.state.user || '' })
        await this.refreshAssigned()
        this.$root.$message('บันทึกแผนเรียบร้อย',
          `สร้าง ${res.created} · อัปเดต ${res.updated} · ลบ ${res.removed} รายการ`,
          { type: 'alert', color: 'success', width: 420 })
      } catch (e) {
        this.$root.$message('บันทึกไม่สำเร็จ',
          e && e.message === 'ALREADY_DISPATCHED'
            ? this.dispatchedLines(e)
            : ['ไม่สามารถบันทึกแผนได้', String(e && e.message ? e.message : e)],
          { type: 'alert', color: 'error', width: 460 })
        if (e && e.message === 'ALREADY_DISPATCHED') this.runPlan({ keepMode: true })
      } finally {
        this.committing = false
      }
    },

    async askClear () {
      this.confirm = {
        show: true, loading: true, title: 'ล้างแผนที่ระบบจัดไว้', lines: [],
        ok: 'ล้างแผน', color: 'error', manual: 0, go: this.doClear
      }
      try {
        const dry = await D.clearPlan(this.date, { dryRun: true })
        this.confirm.lines = [`จะลบนัดหมายที่ระบบจัดไว้ ${dry.removed} รายการ`]
      } catch (e) {
        this.confirm.lines = ['ตรวจสอบไม่สำเร็จ: ' + (e && e.message ? e.message : e)]
      } finally {
        this.confirm.loading = false
      }
    },

    async doClear () {
      this.confirm.show = false
      this.clearing = true
      try {
        const res = await D.clearPlan(this.date)
        await this.refreshAssigned()
        this.$root.$message('ล้างแผนเรียบร้อย', `ลบ ${res.removed} รายการ`,
          { type: 'alert', color: 'success', width: 400 })
      } catch (e) {
        this.$root.$message('ล้างแผนไม่สำเร็จ', String(e && e.message ? e.message : e),
          { type: 'alert', color: 'error', width: 400 })
      } finally {
        this.clearing = false
      }
    }
  }
}
</script>

<style scoped>
.plan-choice { cursor: pointer; }
.plan-choice--on { border: 2px solid #ff9100 !important; }
/* Five cars means five tables, and an auto layout sizes each one to its own
   content -- which is why the clock columns used to step sideways from one car
   to the next. Fixed layout pins them; the notes column takes what is left. */
.plan-table >>> table {
  table-layout: fixed;
  min-width: 1080px;
}

/* The opened detail hangs off the row above it, so it must not read as another
   stop. A tint and no row divider under the timetable line are what join the
   two; the left rule points back at the chevron that opened it. */
/* TRANSITION PERIOD: a row whose SO is already on คิวรถเล็ก. */
.dup-row,
.plan-table .dup-row > td {
  background: #ffebee !important;
}

.plan-table .detail-row > td {
  background: #fafafa;
  border-left: 3px solid #ff9800;
}

/* Rows are two and three lines tall (name over ตำบล). Centring every cell in
   that height floats the times off the name they belong to, so align to the
   top instead and let the caption lines hang below. */
.plan-table td {
  vertical-align: top;
  padding-top: 6px;
  padding-bottom: 6px;
}

.plan-table .nowrap {
  white-space: nowrap;
}

/* A wrapped "ลำดั/บ" is the giveaway that a width forgot Vuetify pads every
   cell by 16px on each side. Headers are short; none of them should wrap. */
.plan-table th {
  white-space: nowrap;
}

/* Small enough to sit in the toolbar without competing with the plate. */
.depart-field >>> input {
  text-align: center;
  font-size: 0.85rem;
}
</style>
